import { HitsReportDataRequestDTO } from 'business/models/Models';
import ScoreGroupService from 'business/services/PlateService';
import { TreeService } from 'business/services/TreeService';
import { RMCheckBoxY } from 'components/customComponents/Controls/RMCheckbox';
import { RMModal, LoadingModal } from 'components/modal';
import React, { useEffect, useMemo, useState } from 'react';
import { createHitReportPDF, HitsReportOptions } from './HitReportPDF';
import { Table } from 'components/main/ProteinFormulation/ProteinIngredients';
import { CaptureProfileOption } from './ExperimentReportOptions';
import { FmlxModal } from 'fmlx-common-ui';

type ScoreGroupOption = {
  id: number;
  name: string;
  selected: boolean;
};

export const HitsReportOptionsDialogue = ({
  nodeIds,
  show,
  setShow,
  showNoHitError,
  setShowNoHitError,
  maxNumOfDrops,
}: any) => {
  const [scoreGroups, setScoreGroups] = useState<ScoreGroupOption[]>([]);
  const [captureProfiles, setCaptureProfiles] = useState<CaptureProfileOption[]>([]);
  const [allGroupsSelected, setAllGroupsSelected] = useState<boolean>(true);
  const [latestInspectionOnly, setLatestInspectionOnly] = useState<boolean>(false);
  const [uvHitsOnly, setUvHitsOnly] = useState<boolean>(false);
  const [selectedDrop, setSelectedDrop] = useState<number>(1);
  const [isLoading, setIsloading] = useState(false);
  const [okayDisabled, setOkayDisabled] = useState(false);
  const isSherlock4Selected = useMemo(
    () =>
      scoreGroups.some(
        (sg) => sg.selected && sg.name?.trim().toLowerCase() === 'sherlock4',
      ),
    [scoreGroups],
  );

  const columns = useMemo(
    () => [
      { Header: 'Imaging Setting', accessor: 'name', width: '80%' },
      { Header: 'Selected', accessor: 'value', width: '20%' },
    ],
    [],
  );

  useEffect(() => {
    retreiveScoreGroups();
    retrieveCaptureProfiles();
  }, []);

  useEffect(() => {
    setOkayDisabled(scoreGroups.findIndex((sg) => sg.selected === true) < 0);
  }, [scoreGroups]);

  useEffect(() => {
    if (!isSherlock4Selected) {
      setUvHitsOnly(false);
    }
  }, [isSherlock4Selected]);

  async function retreiveScoreGroups() {
    const scoreGroups = await new ScoreGroupService().getScoreGroupsService();
    const scoreGroupOptions = scoreGroups.map(
      (sg) =>
        ({
          id: sg.scoreGroupsId,
          name: sg.name,
          selected: true,
        } as ScoreGroupOption),
    );
    setScoreGroups(scoreGroupOptions);
  }

  async function retrieveCaptureProfiles() {
    const captureProfiles = await new ScoreGroupService().getCaptureProfiles();
    const captureProfileOptions = captureProfiles.captureProfileRows.map(
      (cp) => ({ id: cp.id, name: cp.name, selected: false } as CaptureProfileOption),
    );
    setCaptureProfiles(captureProfileOptions);
  }

  async function hitReportsHandler(saveReport: boolean) {
    const treeService = new TreeService();
    if (!uvHitsOnly) {
      const isHitAvailable = await treeService.IsAnyTreeNodeContainHits(nodeIds);
      if (!isHitAvailable) {
        setShowNoHitError(true);
        return;
      }
    }
    setIsloading(true);
    const scoreGroupIds = scoreGroups
      .filter((sg) => sg.selected === true)
      .map((sg) => sg.id);
    const hitsReportDataRequestDTO: HitsReportDataRequestDTO =
      new HitsReportDataRequestDTO({
        nodeIds,
        dropNumbers: [selectedDrop],
        scoreGroupIds,
        captureProfiles: captureProfiles
          .filter((cp) => cp.selected === true)
          .map((cp) => cp.id),
        latestOnly: latestInspectionOnly,
        uvHitsOnly,
      });
    const options: HitsReportOptions = {
      latestInspectionOnly: latestInspectionOnly,
      uvHitsOnly: uvHitsOnly,
    };

    const data = await treeService.getHitReportData(hitsReportDataRequestDTO);
    createHitReportPDF(data, saveReport, options);
    setIsloading(false);
    setShow(false);
  }

  function captureProfilesChangeHandler(flag: boolean, profile: CaptureProfileOption) {
    captureProfiles.find((p) => p.id == profile.id).selected = flag;
    setCaptureProfiles([...captureProfiles]);
  }

  function scoreGroupsChangeHandler(flag: boolean, group: ScoreGroupOption) {
    scoreGroups.find((sg) => sg.id == group.id).selected = flag;
    const selectedScoresGroupsCount = scoreGroups.filter(
      (item) => item.selected === true,
    ).length;
    setScoreGroups([...scoreGroups]);
    setAllGroupsSelected(scoreGroups.length === selectedScoresGroupsCount);
  }

  function allGroupSelectionHandler(flag: boolean) {
    scoreGroups.forEach((sg) => (sg.selected = flag));
    setScoreGroups([...scoreGroups]);
    setAllGroupsSelected(flag);
  }
  function createOptions() {
    const options = [];
    for (let i = 1; i <= maxNumOfDrops; i++) {
      options.push(
        <option key={'option_' + i} value={i}>
          {i}
        </option>,
      );
    }
    return options;
  }

  async function previewReport() {
    setShow(false);
    await hitReportsHandler(false);
  }

  async function saveReport() {
    setShow(false);
    await hitReportsHandler(true);
  }

  return (
    <>
      <RMModal
        id="hitsreportoptionsdialogue-modal-wrapper"
        showModal={show}
        setShowModal={setShow}
        dialogClassName="modal-HitReport"
        body={
          <div
            id="hitsreportoptionsdialogue-modal-container"
            className="dashed-border p-[0.5%] h-[62vh] mb-[1vh] flex flex-col"
          >
            <div id="hitsreportoptionsdialogue-modal-information-text" className="h-[7%]">
              Please provide information you want to include into the report
            </div>
            <div
              id="hitsreportoptionsdialogue-modal-content-section"
              className="flex flex-row h-[93%]"
            >
              <div
                id="hitsreportoptionsdialogue-modal-drop-score-container"
                className="flex-50 flex flex-col divider-right h-[100%]"
              >
                <div
                  id="hitsreportoptionsdialogue-modal-drop-section"
                  className="flex flex-row flex-gap-1 ml-2 h-[7%] items-center"
                >
                  Drop
                  <select
                    name="liquidClass"
                    id="hitsreportoptionsdialogue-modal-drop-liquidClass"
                    onChange={(e) => {
                      setSelectedDrop(parseInt(e.target.value));
                    }}
                    value={selectedDrop}
                    style={{ width: '4rem' }}
                    className="inputFieldDetails"
                  >
                    {createOptions()}
                  </select>
                </div>
                <div
                  id="hitsreportoptionsdialogue-modal-score-section"
                  className="flex flex-col flex-gap-1 h-[79%]"
                >
                  <div
                    id="hitsreportoptionsdialogue-modal-score-select-all-checkbox"
                    className="flex flex-row align-items-baseline"
                  >
                    <RMCheckBoxY
                      isChecked={allGroupsSelected}
                      onChange={allGroupSelectionHandler}
                    />
                    <div
                      id="hitsreportoptionsdialogue-modal-score-select-all-text"
                      className="text-label"
                    >
                      Select All Score Groups
                    </div>
                  </div>
                  <div
                    id="hitsreportoptionsdialogue-modal-score-groups"
                    className="m-2"
                    style={{ overflow: 'auto', height: '25rem' }}
                  >
                    {scoreGroups.map((sg, index) => (
                      <div
                        id={`hitsreportoptionsdialogue-modal-score-group-${index}`}
                        key={`hits-reports-score-group-${index}`}
                        className="d-flex flex-row align-items-baseline"
                      >
                        <RMCheckBoxY
                          isChecked={sg.selected}
                          onChange={(e) => {
                            scoreGroupsChangeHandler(e, sg);
                          }}
                        />
                        <div
                          id={`hitsreportoptionsdialogue-modal-score-group-${sg.name}-text`}
                          className="text-label"
                        >
                          {sg.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div
                  id="hitsreportoptionsdialogue-modal-latest-inspection-section"
                  className="d-flex flex-row align-items-baseline h-[7%]"
                >
                  <RMCheckBoxY
                    isChecked={latestInspectionOnly}
                    onChange={(e) => {
                      setLatestInspectionOnly(e);
                    }}
                  />
                  <div
                    id="hitsreportoptionsdialogue-modal-latest-inspection-text"
                    className="text-label"
                  >
                    Use score from latest inspection only
                  </div>
                </div>
                <div
                  id="hitsreportoptionsdialogue-modal-uv-hits-only-section"
                  className="d-flex flex-row align-items-baseline h-[7%]"
                >
                  <RMCheckBoxY
                    isChecked={uvHitsOnly}
                    disabled={!isSherlock4Selected}
                    onChange={(e) => {
                      setUvHitsOnly(e);
                    }}
                  />
                  <div
                    id="hitsreportoptionsdialogue-modal-uv-hits-only-text"
                    className="text-label"
                    style={{ opacity: isSherlock4Selected ? 1 : 0.6 }}
                  >
                    UV Hits only
                  </div>
                </div>
              </div>
              <div
                id="hitsreportoptionsdialogue-modal-capture-profile-section"
                className="flex-50 flex flex-col h-[100%]"
              >
                <div
                  id="hitsreportoptionsdialogue-modal-capture-profile-table"
                  className="h-[95%] overflow-y-auto"
                >
                  <Table
                    classNames="h-[100%]"
                    columns={columns}
                    data={captureProfiles.map((val) => {
                      return {
                        name: val.name,
                        value: (
                          <RMCheckBoxY
                            disabled={
                              !val.selected &&
                              captureProfiles.filter((cp) => cp.selected === true)
                                .length == 2
                            }
                            isChecked={val.selected}
                            onChange={(e) => captureProfilesChangeHandler(e, val)}
                          />
                        ),
                      };
                    })}
                  />
                </div>
                <div
                  id="hitsreportoptionsdialogue-modal-capture-profile-status"
                  className="h-[5%] content-center"
                >
                  <div
                    id="hitsreportoptionsdialogue-modal-capture-profile-maximum-selection-status"
                    className="text-p text-error"
                  >
                    {' '}
                    {captureProfiles.filter((cp) => cp.selected === true).length == 2
                      ? 'Maximum seletion reached!'
                      : ''}{' '}
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
        onOk={previewReport}
        onSave={saveReport}
        okayDisabled={okayDisabled}
        okButtonText="Preview"
        noButtonText="Close"
        saveButtonText="Save"
        isSaveAvailable={true}
        heading="Hits Report Options"
        size={FmlxModal.Size.EXTRA_LARGE}
      />
      <LoadingModal showModal={isLoading} text={'Loading'} />
      <RMModal
        id="hitsreportoptionsdialogue-configurehits-modal"
        showModal={showNoHitError}
        setShowModal={setShowNoHitError}
        body="You have no hits here. Have you configured hits?"
        heading="Error"
        onOk={() => {
          setShowNoHitError(false);
        }}
        okButtonText="OK"
        isOkOnly
      />
    </>
  );
};
